import {
  Card,
  CardContent,
  Typography,
  Stack,
  Box,
  Button,
  Skeleton,
} from "@mui/material";

import { MY_PERF_SCROLL_LIST_PB } from "../../Dashboard/layouts";

function formatInsightDate(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function InsightRow({ row, onExplore }) {
  return (
    <Box
      sx={{
        border: "1px solid #E7ECF0",
        borderRadius: "10px",
        p: 1.5,
        bgcolor: "#FFFFFF",
      }}
    >
      <Stack spacing={0.6}>
        <Stack direction="row" justifyContent="space-between" gap={1}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: "#2D2D2D",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.name || "-"}
          </Typography>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: "#64748B",
              lineHeight: 1.2,
              flexShrink: 0,
            }}
          >
            {formatInsightDate(row.date)}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" gap={1} alignItems="center">
          <Typography
            sx={{
              fontSize: 12,
              color: "#718096",
              mt: "2px",
              lineHeight: 1.2,
            }}
          >
            {row.details || "No description available"}
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={() => onExplore?.(row)}
            sx={{
              textTransform: "none",
              fontSize: 14,
              fontWeight: 600,
              color: "#D96B04",
              minWidth: "auto",
              p: 0,
              alignSelf: "flex-end",
            }}
          >
            Explore
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

function InsightSkeletonRows({ count = 3 }) {
  return (
    <Stack spacing={1.5}>
      {Array.from({ length: count }).map((_, idx) => (
        <Box
          key={idx}
          sx={{
            border: "1px solid #E7ECF0",
            borderRadius: "10px",
            p: 1.5,
            bgcolor: "#FFFFFF",
          }}
        >
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Skeleton variant="text" width="24%" height={13} />
              <Skeleton variant="text" width={110} height={12} />
            </Stack>
            <Skeleton variant="text" width="100%" height={12} />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Skeleton variant="text" width={90} height={12} />
            </Box>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

const DEFAULT_LIST_MAX_HEIGHT = 400;

export default function InsightCategoryCard({
  title,
  badgeColor = "#F59E0B",
  badgeText = "INSIGHTS",
  rows = [],
  isLoading = false,
  onExplore,
  emptyMessage = "No insights found",
  listMaxHeight = DEFAULT_LIST_MAX_HEIGHT,
}) {
  const hasRows = Array.isArray(rows) && rows.length > 0;

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: "12px",
        border: "1px solid #E5E7EB",
        boxShadow: "1px 4px 4px 0px #00000008",
      }}
    >
      <CardContent
        sx={{
          p: 3,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ flexShrink: 0, mb: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: 1.2,
                }}
              >
                {title}
              </Typography>
              <Box
                sx={{
                  px: 1,
                  py: 0.2,
                  borderRadius: "999px",
                  bgcolor: badgeColor,
                  color: "#FFFFFF",
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.3,
                }}
              >
                {badgeText}
              </Box>
            </Stack>
          </Stack>
        </Box>

        <Box
          sx={{
            flex: "1 1 0",
            minHeight: 0,
            maxHeight: listMaxHeight,
            overflowY: "auto",
            overflowX: "hidden",
            pr: 0.5,
            pb: MY_PERF_SCROLL_LIST_PB,
          }}
        >
          {isLoading ? (
            <InsightSkeletonRows />
          ) : !hasRows ? (
            <Box
              sx={{
                minHeight: 120,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography sx={{ color: "#718096", fontSize: 14 }}>
                {emptyMessage}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {rows.map((row, idx) => (
                <InsightRow key={`${row.name}-${idx}`} row={row} onExplore={onExplore} />
              ))}
            </Stack>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
